import { Component, ChangeDetectionStrategy, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HubConnectionState } from '@microsoft/signalr';
import { SignalRService } from './access/signalr.service';

interface ConnectionIndicator {
  color: string;
  title: string;
  pulse: boolean;
}

@Component({
  selector: 'of-connection-status',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        align-items: center;
        display: inline-flex;
      }
    `
  ],
  template: `
    @if (indicator(); as dot) {
      <span
        class="of-conn-dot"
        [class.of-conn-dot--overlay]="overlay()"
        [class.of-conn-dot--pulse]="dot.pulse"
        [style.background]="dot.color"
        [title]="dot.title"
      ></span>
      @if (!overlay()) {
        <span class="of-conn-label" [style.color]="dot.color">{{ dot.title }}</span>
      }
    }
  `
})
export class ConnectionStatusBadgeComponent {
  private readonly signalrService = inject(SignalRService);

  // true = absolutely-positioned corner badge (parent needs position: relative), no text label.
  // false (default) = inline dot + text label, for toolbars/page headers.
  public readonly overlay = input(false);

  private readonly connectionState = toSignal(this.signalrService.connectionState$, {
    initialValue: HubConnectionState.Disconnected
  });

  // null (hidden) when Connected — this indicator only needs to surface problems.
  protected readonly indicator = computed<ConnectionIndicator | null>(() => {
    const state = this.connectionState();
    if (state === HubConnectionState.Connected) return null;
    if (state === HubConnectionState.Connecting || state === HubConnectionState.Reconnecting) {
      return { color: 'var(--bs-warning-text-emphasis)', title: state as string, pulse: true };
    }
    return { color: 'var(--bs-danger-text-emphasis)', title: 'Disconnected', pulse: false };
  });
}
